/**
 * components/procurement/ScenarioEditor.jsx — model a budget scenario.
 *
 * Phase 5a modelling tools: supply/delivery escalation, an effective tax %
 * override, and a list of added charges (percentage or absolute). The full
 * per-period rate grid, contract/market linkage, CSV upload and AI prompting
 * arrive in 5b/5c. Stores assumptions only — the engine computes on load.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Loader2 } from 'lucide-react';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import { createScenario, patchScenario } from '../../api/budgets';

const blankAssumptions = () => ({
  escalation: { supply: '', delivery: '' },
  tax_pct: '',
  other_charges_pct: '',
  added_charges: [],
  source: 'manual',
  rate_overrides: [],
  water_components: {},
  notes: '',
});

const APPLIES = [
  { value: 'total', label: 'Total' },
  { value: 'supply', label: 'Supply' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'energy', label: 'Supply + Delivery' },
];

const WATER_COMP_FIELDS = [
  { key: 'water_supply', label: 'Water Supply (fixed)' },
  { key: 'water_usage', label: 'Water Usage' },
  { key: 'sewer_access', label: 'Sewer Access (fixed)' },
  { key: 'sewer_usage', label: 'Sewer Usage' },
  { key: 'stormwater', label: 'Stormwater (fixed)' },
];

export function ScenarioEditor({ open, onOpenChange, accountId, budgetId, existing, commodities, onSaved }) {
  const isEdit = !!existing;
  const isWater = (commodities || []).some((c) => c === 'water' || c === 'sewer')
    && !(commodities || []).some((c) => c === 'electricity' || c === 'gas');
  const isElecGas = (commodities || []).some((c) => c === 'electricity' || c === 'gas');
  const [name, setName] = useState('');
  const [a, setA] = useState(blankAssumptions());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setName(existing.name || '');
      const ex = existing.assumptions || {};
      setA({
        escalation: { supply: ex.escalation?.supply ?? '', delivery: ex.escalation?.delivery ?? '' },
        tax_pct: ex.tax_pct ?? '',
        other_charges_pct: ex.other_charges_pct ?? '',
        added_charges: (ex.added_charges || []).map((c) => ({ ...c })),
        source: ex.source || 'manual',
        rate_overrides: (ex.rate_overrides || []).map((o) => ({
          commodity: o.commodity || '', property_key: o.property_key || '',
          period_from: o.period_from || '', period_to: o.period_to || '',
          supply_rate: o.supply_rate ?? '', delivery_rate: o.delivery_rate ?? '',
        })),
        water_components: ex.water_components || {},
        notes: ex.notes || '',
      });
    } else {
      setName('');
      setA(blankAssumptions());
    }
  }, [open, existing]);

  const num = (v) => (v === '' || v == null ? null : Number(v));
  const updWaterComp = (key, val) => setA((s) => ({ ...s, water_components: { ...(s.water_components || {}), [key]: val } }));

  const addCharge = () => setA((s) => ({ ...s, added_charges: [...s.added_charges, { label: '', kind: 'pct', value: 0, applies_to: 'total' }] }));
  const updCharge = (i, patch) => setA((s) => ({ ...s, added_charges: s.added_charges.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) }));
  const delCharge = (i) => setA((s) => ({ ...s, added_charges: s.added_charges.filter((_, idx) => idx !== i) }));

  const addOverride = () => setA((s) => ({
    ...s,
    rate_overrides: [...(s.rate_overrides || []), {
      commodity: (commodities || [])[0] || '', period_from: '', period_to: '',
      supply_rate: '', delivery_rate: '',
    }],
  }));
  const updOverride = (i, patch) => setA((s) => ({
    ...s, rate_overrides: (s.rate_overrides || []).map((o, idx) => (idx === i ? { ...o, ...patch } : o)),
  }));
  const delOverride = (i) => setA((s) => ({ ...s, rate_overrides: (s.rate_overrides || []).filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!name.trim()) { toast.error('Scenario name is required'); return; }
    setSaving(true);
    const assumptions = {
      escalation: { supply: num(a.escalation.supply), delivery: num(a.escalation.delivery) },
      tax_pct: num(a.tax_pct),
      other_charges_pct: isElecGas ? num(a.other_charges_pct) : null,
      added_charges: a.added_charges
        .filter((c) => c.label.trim())
        .map((c) => ({ label: c.label.trim(), kind: c.kind, value: Number(c.value) || 0, applies_to: c.applies_to })),
      rate_overrides: (a.rate_overrides || [])
        .filter((o) => o.supply_rate !== '' || o.delivery_rate !== '')
        .map((o) => ({
          commodity: o.commodity || null,
          ...(o.property_key ? { property_key: o.property_key } : {}),
          period_from: o.period_from || null,
          period_to: o.period_to || null,
          supply_rate: num(o.supply_rate),
          delivery_rate: num(o.delivery_rate),
        })),
      source: a.source,
      notes: a.notes || null,
    };
    if (isWater) {
      const wc = {};
      WATER_COMP_FIELDS.forEach(({ key }) => {
        const v = num((a.water_components || {})[key]);
        if (v != null) wc[key] = v;
      });
      if (Object.keys(wc).length) assumptions.water_components = wc;
    }
    try {
      if (isEdit) {
        await patchScenario({ accountId, id: budgetId, scenarioId: existing.id, patch: { name: name.trim(), assumptions } });
        toast.success('Scenario updated');
      } else {
        await createScenario({ accountId, id: budgetId, payload: { name: name.trim(), assumptions } });
        toast.success('Scenario created');
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error('Save failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="scenario-editor">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Scenario' : 'New Scenario'}</DialogTitle>
          <DialogDescription>
            Model rates, escalation, tax and added charges. Calculations are applied on load —
            nothing heavy is stored.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="s-name">Scenario name</Label>
            <Input id="s-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Market +8% / Contracted Block" data-testid="scenario-name-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="s-esc-s">{isWater ? 'Water escalation % / yr' : 'Supply escalation % / yr'}</Label>
              <Input id="s-esc-s" type="number" step="0.5" value={a.escalation.supply}
                onChange={(e) => setA((s) => ({ ...s, escalation: { ...s.escalation, supply: e.target.value } }))}
                placeholder="default" data-testid="scenario-esc-supply" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="s-esc-d">{isWater ? 'Sewer escalation % / yr' : 'Delivery escalation % / yr'}</Label>
              <Input id="s-esc-d" type="number" step="0.5" value={a.escalation.delivery}
                onChange={(e) => setA((s) => ({ ...s, escalation: { ...s.escalation, delivery: e.target.value } }))}
                placeholder="default" data-testid="scenario-esc-delivery" />
            </div>
          </div>

          {isWater && (
            <div className="space-y-2 rounded-md border bg-sky-50/40 p-3" data-testid="water-components-section">
              <div>
                <Label className="text-sky-900">Water &amp; Sewer components — annual % increase</Label>
                <p className="text-xs text-muted-foreground">Dial in a per-component rate increase. Blank components fall back to the water/sewer escalation above.</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {WATER_COMP_FIELDS.map(({ key, label }) => (
                  <div key={key} className="space-y-1">
                    <Label htmlFor={`wc-${key}`} className="text-xs font-normal">{label}</Label>
                    <div className="relative">
                      <Input id={`wc-${key}`} type="number" step="0.5" className="pr-6"
                        value={(a.water_components || {})[key] ?? ''}
                        onChange={(e) => updWaterComp(key, e.target.value)}
                        placeholder="default" data-testid={`scenario-wc-${key}`} />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="s-tax">Effective tax % (override)</Label>
              <Input id="s-tax" type="number" step="0.5" value={a.tax_pct}
                onChange={(e) => setA((s) => ({ ...s, tax_pct: e.target.value }))}
                placeholder="from history" data-testid="scenario-tax" />
            </div>
            {isElecGas ? (
              <div className="space-y-1.5">
                <Label htmlFor="s-other">Other charges % / yr</Label>
                <Input id="s-other" type="number" step="0.5" value={a.other_charges_pct}
                  onChange={(e) => setA((s) => ({ ...s, other_charges_pct: e.target.value }))}
                  placeholder="from history" data-testid="scenario-other-charges" />
                <p className="text-[11px] text-muted-foreground">Riders, surcharges, demand/capacity &amp; fees.</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>Source</Label>
                <Select value={a.source} onValueChange={(v) => setA((s) => ({ ...s, source: v }))}>
                  <SelectTrigger data-testid="scenario-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="contract">Contracted rates</SelectItem>
                    <SelectItem value="market">Market rates</SelectItem>
                    <SelectItem value="csv">CSV upload</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          {isElecGas && (
            <div className="space-y-1.5">
              <Label>Source</Label>
              <Select value={a.source} onValueChange={(v) => setA((s) => ({ ...s, source: v }))}>
                <SelectTrigger data-testid="scenario-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="contract">Contracted rates</SelectItem>
                  <SelectItem value="market">Market rates</SelectItem>
                  <SelectItem value="csv">CSV upload</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Added charges</Label>
              <Button size="sm" variant="outline" onClick={addCharge} data-testid="scenario-add-charge">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add
              </Button>
            </div>
            {a.added_charges.length === 0 && (
              <p className="text-xs text-muted-foreground">No extra charges. Add taxes, fees or management charges as % or $.</p>
            )}
            {a.added_charges.map((c, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`scenario-charge-${i}`}>
                <Input className="flex-1" value={c.label} placeholder="Label"
                  onChange={(e) => updCharge(i, { label: e.target.value })} />
                <Select value={c.kind} onValueChange={(v) => updCharge(i, { kind: v })}>
                  <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pct">%</SelectItem>
                    <SelectItem value="absolute">$</SelectItem>
                  </SelectContent>
                </Select>
                <Input className="w-20" type="number" value={c.value}
                  onChange={(e) => updCharge(i, { value: e.target.value })} />
                <Select value={c.applies_to} onValueChange={(v) => updCharge(i, { applies_to: v })}>
                  <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {APPLIES.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="icon" variant="ghost" onClick={() => delCharge(i)}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>

          <div className="space-y-2 rounded-md border bg-amber-50/40 p-3" data-testid="rate-overrides-section">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-amber-900">Rate overrides ($/unit)</Label>
                <p className="text-[11px] text-muted-foreground">
                  Fixed supply/delivery $/unit for a period (overrides historical + escalation).
                  AI, contract &amp; market scenarios populate these — review &amp; edit them here.
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={addOverride} data-testid="scenario-add-override">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add
              </Button>
            </div>
            {(a.rate_overrides || []).length === 0 && (
              <p className="text-xs text-muted-foreground">No rate overrides — the budget uses each property's historical $/unit, escalated by the %/yr above.</p>
            )}
            {(a.rate_overrides || []).length > 6 && (
              <p className="text-[11px] text-muted-foreground">{a.rate_overrides.length} overrides (e.g. a monthly market curve). Scroll to review each.</p>
            )}
            <div className="max-h-56 overflow-y-auto space-y-2 pr-1">
              {(a.rate_overrides || []).map((o, i) => (
                <div key={i} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`scenario-override-${i}`}>
                  <Select value={o.commodity || ''} onValueChange={(v) => updOverride(i, { commodity: v })}>
                    <SelectTrigger className="col-span-3 h-8 text-xs" data-testid={`override-commodity-${i}`}><SelectValue placeholder="Commodity" /></SelectTrigger>
                    <SelectContent>
                      {(commodities || []).map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input className="col-span-2 h-8 text-xs" placeholder="from" value={o.period_from}
                    onChange={(e) => updOverride(i, { period_from: e.target.value })} title="YYYY-MM (optional)" />
                  <Input className="col-span-2 h-8 text-xs" placeholder="to" value={o.period_to}
                    onChange={(e) => updOverride(i, { period_to: e.target.value })} title="YYYY-MM (optional)" />
                  <Input className="col-span-2 h-8 text-xs" type="number" step="0.0001" placeholder="supply $" value={o.supply_rate}
                    onChange={(e) => updOverride(i, { supply_rate: e.target.value })} title="Supply $/unit" data-testid={`override-supply-${i}`} />
                  <Input className="col-span-2 h-8 text-xs" type="number" step="0.0001" placeholder="deliv $" value={o.delivery_rate}
                    onChange={(e) => updOverride(i, { delivery_rate: e.target.value })} title="Delivery $/unit" />
                  <Button size="icon" variant="ghost" className="col-span-1 h-8 w-8" onClick={() => delOverride(i)}>
                    <Trash2 className="w-3.5 h-3.5 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="s-notes">Notes</Label>
            <Textarea id="s-notes" rows={2} value={a.notes}
              onChange={(e) => setA((s) => ({ ...s, notes: e.target.value }))} data-testid="scenario-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="scenario-save-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : (isEdit ? 'Save Scenario' : 'Create Scenario')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
